import { useState, useEffect, useRef } from "react";
import { fetchToken, onMessageListener } from "../firebase/config";
import { notification } from "antd";
import { useHistory, useLocation } from "react-router-dom"; // Essential for history.push in classes
import axios from "../utils/axios";
import { getStore, setStore } from "../utils/global";

const FCMManager = ({ children }) => {
  const [token, setToken] = useState(null);
  const [notifPermissionStatus, setNotifPermissionStatus] = useState(null);
  const retryLoadToken = useRef(0);
  const isLoading = useRef(false);
  const unsubscribe = useRef(null);
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const user = getStore("user");
    if (user && "Notification" in window && retryLoadToken.current < 3) {
      loadToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const user = getStore("user");

    if (user && !token && !isLoading.current && retryLoadToken.current < 3) {
      loadToken();
    }

    if (user && token) {
      syncTokenWithBackend();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, location.pathname]);

  const syncTokenWithBackend = async () => {
    const user = getStore("user");
    const isSynced = getStore("fcm_token_synced"); // Check if already registered

    if (!user || !token || isSynced === token) {
      return;
    }

    try {
      await axios.post("/client/api/notification/register", { fcm_token: token, device_type: "web" });
      setStore("fcm_token_synced", token);
      retryLoadToken.current = 0;
    } catch (error) {
      if (error?.data?.code === 409 && token) {
        setStore("fcm_token_synced", token);
        retryLoadToken.current = 0;
      }
    }
  };

  useEffect(() => {
    return () => {
      if (unsubscribe.current) {
        unsubscribe.current();
      }
    };
  }, []);

  const getNotificationPermissionAndToken = async () => {
    if (!("Notification" in window)) {
      console.log("This browser does not support desktop notification");
      return null;
    }

    if (Notification.permission === "granted") {
      return await fetchToken();
    }

    if (Notification.permission !== "denied") {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        return await fetchToken();
      }
    }

    return null;
  };

  const loadToken = async () => {
    if (isLoading.current) return;
    isLoading.current = true;

    const fcmToken = await getNotificationPermissionAndToken();

    if (Notification.permission === "denied") {
      setNotifPermissionStatus("denied");
      isLoading.current = false;
      return;
    }

    if (!fcmToken) {
      if (retryLoadToken.current >= 3) {
        console.warn("FCM: Could not retrieve token after 3 attempts.");
        isLoading.current = false;
        return;
      }
      retryLoadToken.current += 1;
      isLoading.current = false;
      await loadToken();
      return;
    }

    setNotifPermissionStatus(Notification.permission);
    setToken(fcmToken);
    isLoading.current = false;
  };

  const setupListener = async () => {
    if (!token) return;

    if (unsubscribe.current) unsubscribe.current();

    unsubscribe.current = await onMessageListener((payload) => {
      if (Notification.permission !== "granted") return;

      const title = payload.notification?.title || "New Message";
      const body = payload.notification?.body || "";
      const link = payload.fcmOptions?.link || payload.data?.link;

      notification.info({
        message: title,
        description: body,
        duration: 3,
        onClick: () => {
          if (link) history.push(link);
        },
      });

      const n = new Notification(title, {
        body: body,
        data: link ? { url: link } : undefined,
      });

      n.onclick = (event) => {
        event.preventDefault();
        const clickLink = event.target?.data?.url;
        if (clickLink) {
          history.push(clickLink);
          window.focus();
        }
        n.close();
      };
    });
  };

  useEffect(() => {
    setupListener();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return children ? children({ token, status: notifPermissionStatus }) : null;
};

export default FCMManager;
